import {forwardRef} from 'react'
import ShimmerButton from 'components/magicui/ShimmerButton'
import {cn} from 'utils/cn'

/**
 * Magic UI's Shimmer Button in Sharpenny gold.
 *
 * - `primary`: the spend CTA ("Spend 50 pennies", "Awesome!"). Dark
 *   gold ground, cream text, warm spark.
 * - `chip`: the header balance. Pale ground, gold text, slow spark.
 * - `quiet`: for rows of small spend buttons (profile boosts) — white
 *   ground with only the gold spark, so four in a row do not strobe.
 *
 * `disabled` dims the button and stops the spark.
 */
const VARIANTS = {
  primary: {
    background: 'linear-gradient(160deg,#b8801f 0%,#a06a17 45%,#7a4f10 100%)',
    shimmerColor: '#ffedb8',
    shimmerDuration: '3s',
    shimmerSize: '0.07em',
    borderRadius: '100px',
    className:
      'gap-2 px-5 py-2.5 text-sm font-bold text-[#fff4cf] shadow-[0_1px_2px_rgba(90,58,8,.18)] hover:shadow-[0_1px_2px_rgba(90,58,8,.2),0_4px_14px_rgba(201,154,46,.35)] transition-shadow',
  },
  chip: {
    background: 'linear-gradient(160deg,#fff9e3 0%,#ffeebc 100%)',
    shimmerColor: '#c99a2e',
    shimmerDuration: '5s',
    shimmerSize: '0.06em',
    borderRadius: '100px',
    className:
      'gap-1.5 px-3 py-1.5 text-sm font-bold text-[#7a4f10] border-[#c99a2e]/45 shadow-[0_1px_2px_rgba(90,58,8,.08)] hover:shadow-[0_2px_8px_rgba(201,154,46,.3)] transition-shadow',
  },
  quiet: {
    background: '#ffffff',
    shimmerColor: '#e6c25a',
    shimmerDuration: '3s',
    shimmerSize: '0.06em',
    borderRadius: '6px',
    className:
      'gap-2 px-4 py-1.5 text-sm font-medium text-gray-700 border-gray-300 shadow-sm hover:shadow-[0_2px_8px_rgba(201,154,46,.25)] transition-shadow',
  },
}

const GoldButton = forwardRef(
  ({variant = 'primary', className, children, ...props}, ref) => {
    const v = VARIANTS[variant] || VARIANTS.primary
    return (
      <ShimmerButton
        ref={ref}
        background={v.background}
        shimmerColor={v.shimmerColor}
        shimmerDuration={v.shimmerDuration}
        shimmerSize={v.shimmerSize}
        borderRadius={v.borderRadius}
        className={cn(
          v.className,
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:[&_[data-shimmer-spark]]:hidden',
          className
        )}
        {...props}
      >
        {children}
      </ShimmerButton>
    )
  }
)

GoldButton.displayName = 'GoldButton'

export default GoldButton
